// =============================================================================
// DISPLAY PREFERENCES STORE
// =============================================================================
//
// One persisted, client-side store for per-device display preferences. It is
// deliberately NOT on the `users` table: theming is a device choice (a shared
// family tablet in the kitchen wants dark at night regardless of who is logged
// in) and keeping it client-side avoids a users-column migration.
//
// App.tsx maps this store onto the root element that already carries
// `data-comfort`:
//   theme         -> data-theme="dark"       (tokens live in globals.css)
//   highContrast  -> data-high-contrast       (ui_contrast dial)
//   dyslexiaFont  -> data-dyslexia            (device-local)
//   reduceMotion  -> data-reduce-motion        (ui_motion dial)
//   fontScale     -> --user-font-scale         (ui_scale dial, via the root rem)
//
// Two classes of preference live here (mode-system brief section 1):
//   - Device-local: theme, dyslexiaFont, useSystemTextSize. A shared family
//     tablet wants these per-device (dark at night regardless of who is logged
//     in; the OS text size is itself per-device). Persisted to localStorage.
//   - Account-scoped legibility: fontScale (ui_scale), highContrast
//     (ui_contrast), reduceMotion (ui_motion). These follow the person across
//     devices, so the server is the source of truth. They are deliberately NOT
//     persisted (see partialize below) - App seeds them from the authenticated
//     user via hydrateFromServer(), which avoids one device's cache leaking a
//     previous user's large-text setting onto the next person on a shared iPad.
//     The trade-off is a brief default-size paint on reload until hydration
//     runs; acceptable, and safer than a cross-user leak.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

// Stepped legibility notches as a percentage of the browser base. 100 = no-op.
export const SCALE_STEPS = [100, 125, 150, 175, 200] as const;
export const MIN_SCALE = SCALE_STEPS[0];
export const MAX_SCALE = SCALE_STEPS[SCALE_STEPS.length - 1];

// Server prefs -> store seed. Percent column becomes a rem multiplier; the
// 'reduced' motion literal becomes a boolean.
export interface ServerLegibilityPrefs {
  uiScale: number;
  uiContrast: boolean;
  uiMotion: string;
}

interface DisplayPrefsState {
  theme: ThemePreference;
  fontScale: number;
  highContrast: boolean;
  dyslexiaFont: boolean;
  reduceMotion: boolean;
  useSystemTextSize: boolean;
  setTheme: (theme: ThemePreference) => void;
  setFontScale: (fontScale: number) => void;
  setHighContrast: (highContrast: boolean) => void;
  setDyslexiaFont: (dyslexiaFont: boolean) => void;
  setReduceMotion: (reduceMotion: boolean) => void;
  setUseSystemTextSize: (useSystemTextSize: boolean) => void;
  // Seed the account-scoped legibility fields from the authenticated user.
  hydrateFromServer: (prefs: ServerLegibilityPrefs) => void;
  // Reset the Comfort panel to defaults (the "Reset" link).
  resetLegibility: () => void;
}

export const useDisplayPrefsStore = create<DisplayPrefsState>()(
  persist(
    (set) => ({
      theme: 'system',
      fontScale: 1,
      highContrast: false,
      dyslexiaFont: false,
      reduceMotion: false,
      useSystemTextSize: false,
      setTheme: (theme) => set({ theme }),
      setFontScale: (fontScale) => set({ fontScale }),
      setHighContrast: (highContrast) => set({ highContrast }),
      setDyslexiaFont: (dyslexiaFont) => set({ dyslexiaFont }),
      setReduceMotion: (reduceMotion) => set({ reduceMotion }),
      setUseSystemTextSize: (useSystemTextSize) => set({ useSystemTextSize }),
      hydrateFromServer: ({ uiScale, uiContrast, uiMotion }) =>
        set({
          fontScale: uiScale / 100,
          highContrast: uiContrast,
          reduceMotion: uiMotion === 'reduced',
        }),
      resetLegibility: () =>
        set({
          fontScale: 1,
          highContrast: false,
          reduceMotion: false,
          useSystemTextSize: false,
        }),
    }),
    {
      name: 'chlann-display-prefs',
      // Persist device-local fields only. The account-scoped legibility fields
      // come from the server on load (see the header note).
      partialize: (state) => ({
        theme: state.theme,
        dyslexiaFont: state.dyslexiaFont,
        useSystemTextSize: state.useSystemTextSize,
      }),
    }
  )
);

const DARK_QUERY = '(prefers-color-scheme: dark)';

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : false;
}

// Resolve the stored preference into a concrete 'light' | 'dark'. When the
// preference is 'system' it tracks the OS setting live, so a grandparent's
// device flipping to dark at sunset re-themes the app without a reload.
export function useResolvedTheme(): 'light' | 'dark' {
  const theme = useDisplayPrefsStore((s) => s.theme);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(DARK_QUERY);
    const handler = (event: MediaQueryListEvent) => { setSystemDark(event.matches); };
    mq.addEventListener('change', handler);
    return () => { mq.removeEventListener('change', handler); };
  }, []);

  if (theme === 'dark') return 'dark';
  if (theme === 'light') return 'light';
  return systemDark ? 'dark' : 'light';
}
